import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "flowbite-react";
import {
  countiesThunks,
  usersThunks,
  dashboardActions,
  dashboardThunks,
} from "../app/store";
import { RiAuctionLine } from "react-icons/ri";
import { LuLayoutDashboard } from "react-icons/lu";

const STATUS_LABELS = {
  PENDING: "Pendente",
  IN_PROGRESS: "Em andamento",
  WAITING_ADMIN: "Aguardando Admin",
  APPROVED: "Aprovado",
  ABANDONED: "Abandonado",
};

const PAYMENT_LABELS = {
  UNPAID: "Não pago",
  PAID: "Pago",
};

export default function DashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authUser = useSelector((s) => s.auth?.user || null);
  const isAdmin = authUser?.role === "ADMIN";
  const isVA = authUser?.role === "VA";
  const currentUserId = authUser?.id || null;

  const countiesById = useSelector((s) => s.counties?.byId || {});
  const usersById = useSelector((s) => s.users?.byId || {});

  const {
    futureAuctions,
    approvedUnpaidAuctions,
    propsByAuctionId,
    status,
    error,
  } = useSelector((s) => s.dashboard);
  const isLoading = status === "loading";

  const approvedUnpaidStats = useMemo(() => {
    let totalAuctions = approvedUnpaidAuctions.length;
    let totalProps = 0;
    let processed = 0;

    approvedUnpaidAuctions.forEach((a) => {
      const props = propsByAuctionId[a.id] || [];
      totalProps += props.length;
      processed += props.filter((p) => !!p.rating).length;
    });

    return {
      totalAuctions,
      totalProps,
      processed,
    };
  }, [approvedUnpaidAuctions, propsByAuctionId]);

  useEffect(() => {
    if (!authUser?.role || !authUser?.id) return;

    dispatch(countiesThunks.list({ q: "", limit: 9999, offset: 0 }));
    dispatch(usersThunks.list({ q: "", limit: 9999, offset: 0 }));

    dispatch(
      dashboardThunks.load({ role: authUser.role, userId: authUser.id }),
    );
  }, [dispatch, authUser?.role, authUser?.id]);

  // ---------- ADMIN KPIs ----------
  const adminPaymentCounts = useMemo(() => {
    if (!isAdmin) return null;
    const approved = futureAuctions.filter(
      (a) => a?.research_status === "APPROVED",
    );

    let paid = 0;
    let unpaid = 0;
    for (const a of approved) {
      if (a?.payment_status === "PAID") paid += 1;
      else unpaid += 1;
    }
    return { approvedTotal: approved.length, paid, unpaid };
  }, [isAdmin, futureAuctions]);

  const adminStatusMatrix = useMemo(() => {
    if (!isAdmin) return null;

    const matrix = {};
    const statuses = Object.keys(STATUS_LABELS);

    function initRow(key) {
      if (!matrix[key]) {
        matrix[key] = {};
        for (const st of statuses) matrix[key][st] = 0;
      }
    }

    for (const a of futureAuctions) {
      const key = a?.assigned_to_user_id || "__UNASSIGNED__";
      initRow(key);
      const st = a?.research_status || "PENDING";
      matrix[key][st] = (matrix[key][st] || 0) + 1;
    }

    return matrix;
  }, [isAdmin, futureAuctions]);

  const adminApprovedRatingsTable = useMemo(() => {
    if (!isAdmin) return [];

    const approved = futureAuctions
      .filter((a) => a?.research_status === "APPROVED")
      .sort(
        (x, y) =>
          new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime(),
      );

    return approved.map((a) => {
      const props = propsByAuctionId[a.id] || [];
      const A = props.filter((p) => p.rating === "A").length;
      const B = props.filter((p) => p.rating === "B").length;
      const C = props.filter((p) => p.rating === "C").length;
      const REDEEMED = props.filter((p) => p.rating === "REDEEMED").length;

      return { auction: a, total: props.length, A, B, C, REDEEMED };
    });
  }, [isAdmin, futureAuctions, propsByAuctionId]);

  const favoriteProps = useMemo(() => {
    const all = [];

    for (const a of futureAuctions || []) {
      const props = propsByAuctionId?.[a.id] || [];
      for (const p of props) {
        if (p?.is_favorite) {
          all.push({
            auction: a,
            property: p,
          });
        }
      }
    }

    // ordena por auction (mais próximo primeiro) e depois por created_at (se tiver)
    all.sort((x, y) => {
      const ax = x.auction?.starts_at
        ? new Date(x.auction.starts_at).getTime()
        : 0;
      const ay = y.auction?.starts_at
        ? new Date(y.auction.starts_at).getTime()
        : 0;
      if (ax !== ay) return ax - ay;
      const px = x.property?.created_at
        ? new Date(x.property.created_at).getTime()
        : 0;
      const py = y.property?.created_at
        ? new Date(y.property.created_at).getTime()
        : 0;
      return py - px;
    });

    return all;
  }, [futureAuctions, propsByAuctionId]);

  // ---------- VA KPIs ----------
  const vaAuctions = useMemo(() => {
    if (!isVA || !currentUserId) return [];
    return futureAuctions.filter(
      (a) => a?.assigned_to_user_id === currentUserId,
    );
  }, [isVA, currentUserId, futureAuctions]);

  const vaProgressTable = useMemo(() => {
    if (!isVA) return [];
    return vaAuctions
      .slice()
      .sort(
        (x, y) =>
          new Date(x.starts_at).getTime() - new Date(y.starts_at).getTime(),
      )
      .map((a) => {
        const props = propsByAuctionId[a.id] || [];
        const total = props.length;
        const done = props.filter((p) => !!p.rating).length;
        const pending = total - done;

        return { auction: a, total, done, pending };
      });
  }, [isVA, vaAuctions, propsByAuctionId]);

  function countyName(countyId) {
    return countiesById?.[countyId]?.name || "—";
  }

  function userName(userId) {
    if (!userId) return "—";
    const u = usersById?.[userId];
    return u?.name || u?.email || "—";
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <LuLayoutDashboard />
            Dashboard
          </h1>
          {isAdmin && (
            <p className="text-sm text-gray-500">
              Visão:{" "}
              <span className="font-medium">{isAdmin ? "Admin" : "V.A."}</span>
            </p>
          )}
        </div>

        <Button color="gray" onClick={() => navigate("/app/auctions")}>
          <RiAuctionLine className="mr-2" />
          Ir para Auctions
        </Button>
      </div>

      {error && (
        <Alert
          color="failure"
          onDismiss={() => dispatch(dashboardActions.clearError())}
        >
          {error}
        </Alert>
      )}

      {isLoading ? (
        <div className="p-6 flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <Spinner />
          <span className="text-sm text-gray-600 dark:text-gray-300">
            Carregando dashboard...
          </span>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          {isAdmin ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <p className="text-sm text-gray-500">Auctions futuras</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {futureAuctions.length}
                </p>
              </Card>

              <Card>
                <p className="text-sm text-gray-500">Aguardando Admin</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {
                    futureAuctions.filter(
                      (a) => a?.research_status === "WAITING_ADMIN",
                    ).length
                  }
                </p>
              </Card>

              <Card>
                <div className="text-sm text-gray-500 dark:text-purple-300">
                  Approved (Unpaid) – All
                </div>

                <div className="text-2xl font-bold text-gray-900 dark:text-purple-100">
                  {approvedUnpaidStats.totalAuctions}
                </div>

                <div className="text-xs text-gray-500 dark:text-purple-300">
                  {approvedUnpaidStats.processed} /{" "}
                  {approvedUnpaidStats.totalProps} properties processed
                </div>
              </Card>

              <Card>
                <p className="text-sm text-gray-500">Aprovadas (futuras)</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {adminPaymentCounts?.approvedTotal ?? 0}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge
                    color={(adminPaymentCounts?.paid ?? 0) ? "success" : "gray"}
                  >
                    Pagas: {adminPaymentCounts?.paid ?? 0}
                  </Badge>
                  <Badge
                    color={
                      (adminPaymentCounts?.unpaid ?? 0) ? "warning" : "gray"
                    }
                  >
                    Não pagas: {adminPaymentCounts?.unpaid ?? 0}
                  </Badge>
                </div>
              </Card>

              <Card className="md:col-span-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Propriedades Favoritadas
                    </h2>
                    <p className="text-sm text-gray-500">
                      {favoriteProps.length} favorita(s) em auctions futuras
                    </p>
                  </div>

                  <Badge color={favoriteProps.length ? "info" : "gray"}>
                    {favoriteProps.length}
                  </Badge>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <Table hoverable>
                    <TableHead>
                      <TableHeadCell>Auction</TableHeadCell>
                      <TableHeadCell>Parcel</TableHeadCell>
                      <TableHeadCell>Owner</TableHeadCell>
                      <TableHeadCell>Address</TableHeadCell>
                      <TableHeadCell>Ação</TableHeadCell>
                    </TableHead>

                    <TableBody className="divide-y">
                      {favoriteProps.length ? (
                        favoriteProps
                          .slice(0, 20)
                          .map(({ auction, property }) => (
                            <TableRow
                              key={property.id}
                              className="bg-white dark:bg-gray-800"
                            >
                              <TableCell className="font-medium text-gray-900 dark:text-white">
                                {countyName(auction.county_id)}{" "}
                                <span className="text-gray-400">•</span>{" "}
                                {auction?.starts_at
                                  ? new Date(
                                      auction.starts_at,
                                    ).toLocaleDateString()
                                  : "—"}
                              </TableCell>

                              <TableCell>
                                {property.parcel_number || "—"}
                              </TableCell>
                              <TableCell>
                                {property.owner_name || "—"}
                              </TableCell>
                              <TableCell>
                                {property.property_address || "—"}
                              </TableCell>

                              <TableCell>
                                <Button
                                  size="xs"
                                  color="gray"
                                  onClick={() =>
                                    navigate(
                                      `/app/auctions/${auction.id}/properties?card=${property.id}`,
                                    )
                                  }
                                >
                                  Abrir
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                      ) : (
                        <TableRow className="bg-white dark:bg-gray-800">
                          <TableCell
                            colSpan={5}
                            className="text-center py-6 text-gray-500"
                          >
                            Nenhuma propriedade favoritada ainda.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {favoriteProps.length > 20 ? (
                    <p className="mt-3 text-xs text-gray-500">
                      Mostrando 20 de {favoriteProps.length}. (Depois a gente
                      pode criar uma tela “Favoritos”.)
                    </p>
                  ) : null}
                </div>
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <p className="text-sm text-gray-500">Meus auctions futuros</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {vaAuctions.length}
                </p>
              </Card>

              <Card>
                <p className="text-sm text-gray-500">Em andamento</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {
                    vaAuctions.filter(
                      (a) => a?.research_status === "IN_PROGRESS",
                    ).length
                  }
                </p>
              </Card>

              <Card>
                <p className="text-sm text-gray-500">Aguardando Admin</p>
                <p className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {
                    vaAuctions.filter(
                      (a) => a?.research_status === "WAITING_ADMIN",
                    ).length
                  }
                </p>
              </Card>
            </div>
          )}

          {/* ADMIN: matrix por responsável */}
          {isAdmin && adminStatusMatrix && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Auctions futuras por responsável e status
              </h2>

              <div className="mt-4 overflow-x-auto">
                <Table hoverable>
                  <TableHead>
                    <TableHeadCell>Responsável</TableHeadCell>
                    {Object.keys(STATUS_LABELS).map((st) => (
                      <TableHeadCell key={st}>
                        {STATUS_LABELS[st]}
                      </TableHeadCell>
                    ))}
                  </TableHead>

                  <TableBody className="divide-y">
                    {Object.entries(adminStatusMatrix).map(([userId, row]) => (
                      <TableRow
                        key={userId}
                        className="bg-white dark:bg-gray-800"
                      >
                        <TableCell className="font-medium text-gray-900 dark:text-white">
                          {userId === "__UNASSIGNED__"
                            ? "Unassigned"
                            : userName(userId)}
                        </TableCell>

                        {Object.keys(STATUS_LABELS).map((st) => (
                          <TableCell key={st}>
                            <Badge color={row?.[st] ? "info" : "gray"}>
                              {row?.[st] || 0}
                            </Badge>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          {/* ADMIN: Ratings por auction aprovada */}
          {isAdmin && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Aprovadas (futuras): distribuição de Ratings (A/B/C)
              </h2>

              <div className="mt-4 overflow-x-auto">
                <Table hoverable>
                  <TableHead>
                    <TableHeadCell>County</TableHeadCell>
                    <TableHeadCell>Starts At</TableHeadCell>
                    <TableHeadCell>Responsável</TableHeadCell>
                    <TableHeadCell>Total</TableHeadCell>
                    <TableHeadCell>A</TableHeadCell>
                    <TableHeadCell>B</TableHeadCell>
                    <TableHeadCell>C</TableHeadCell>
                    <TableHeadCell>Redeemed</TableHeadCell>
                    <TableHeadCell>Pagamento</TableHeadCell>
                  </TableHead>

                  <TableBody className="divide-y">
                    {adminApprovedRatingsTable.length ? (
                      adminApprovedRatingsTable.map(
                        ({ auction, total, A, B, C, REDEEMED }) => (
                          <TableRow
                            key={auction.id}
                            className="bg-white dark:bg-gray-800"
                          >
                            <TableCell className="font-medium text-gray-900 dark:text-white">
                              {countyName(auction.county_id)}
                            </TableCell>
                            <TableCell>
                              {auction?.starts_at
                                ? new Date(auction.starts_at).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell>
                              {userName(auction.assigned_to_user_id)}
                            </TableCell>
                            <TableCell>
                              <Badge color={total ? "info" : "gray"}>
                                {total}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={A ? "success" : "gray"}>{A}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={B ? "info" : "gray"}>{B}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={C ? "failure" : "gray"}>{C}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={REDEEMED ? "warning" : "gray"}>
                                {REDEEMED}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                color={
                                  auction.payment_status === "PAID"
                                    ? "success"
                                    : "warning"
                                }
                              >
                                {PAYMENT_LABELS[auction.payment_status] ||
                                  auction.payment_status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ),
                      )
                    ) : (
                      <TableRow className="bg-white dark:bg-gray-800">
                        <TableCell
                          colSpan={10}
                          className="text-center py-6 text-gray-500"
                        >
                          Nenhuma auction aprovada futura encontrada.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          {/* VA: progresso por auction */}
          {isVA && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Meu progresso por Auction
              </h2>

              <div className="mt-4 overflow-x-auto">
                <Table hoverable>
                  <TableHead>
                    <TableHeadCell>County</TableHeadCell>
                    <TableHeadCell>Starts At</TableHeadCell>
                    <TableHeadCell>Status</TableHeadCell>
                    <TableHeadCell>Total</TableHeadCell>
                    <TableHeadCell>Feitas</TableHeadCell>
                    <TableHeadCell>Pendentes</TableHeadCell>
                    <TableHeadCell>Ação</TableHeadCell>
                  </TableHead>

                  <TableBody className="divide-y">
                    {vaProgressTable.length ? (
                      vaProgressTable.map(
                        ({ auction, total, done, pending }) => (
                          <TableRow
                            key={auction.id}
                            className="bg-white dark:bg-gray-800"
                          >
                            <TableCell className="font-medium text-gray-900 dark:text-white">
                              {countyName(auction.county_id)}
                            </TableCell>
                            <TableCell>
                              {auction?.starts_at
                                ? new Date(auction.starts_at).toLocaleString()
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <Badge color="info">
                                {STATUS_LABELS[auction.research_status] ||
                                  auction.research_status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={total ? "info" : "gray"}>
                                {total}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={done ? "success" : "gray"}>
                                {done}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge color={pending ? "warning" : "gray"}>
                                {pending}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                size="xs"
                                color="gray"
                                onClick={() =>
                                  navigate(
                                    `/app/auctions/${auction.id}/properties`,
                                  )
                                }
                              >
                                Abrir
                              </Button>
                            </TableCell>
                          </TableRow>
                        ),
                      )
                    ) : (
                      <TableRow className="bg-white dark:bg-gray-800">
                        <TableCell
                          colSpan={8}
                          className="text-center py-6 text-gray-500"
                        >
                          Você não tem auctions futuros atribuídos.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

